// ============================================================================
// rangeStore: o fetchRange do AvecSyncContext com o pedido enxuto (A2c) e o
// cache protegido (A4b), fora do React para dar para testar (23/10/2026).
// ============================================================================
// enxuto (DASH_PEDIDO_ENXUTO): chave por variante (unidades, 0031 paginado,
//   0065), reaproveita chave que traz MAIS, teto de 8 chaves (LRU).
// protegido (RANGE_CACHE_PROTEGIDO): período passado completo fica 2 h em
//   memória (até com force); resultado com falha NUNCA é servido de cache;
//   refazer o MESMO período passado herda o que faltou da busca anterior (a
//   herança não conta como ok para uma nova herança); teto de 8 chaves.
// Nada vai para localStorage (o retorno tem CPF da lista de profissionais e
// comissão por pessoa; e repetiria a crise dos números diferentes por aparelho).
// ALCANCE (revisão 23/10/2026, achado 24): o ganho é SÓ dentro da sessão (ir do
// Dashboard aos Relatórios e voltar). No iPhone o iOS fecha o PWA em segundo
// plano e a reabertura recarrega o JS: o período anterior é buscado de novo a
// cada abertura. Guardar entre aberturas pediria um resumo sem dado pessoal que
// servisse também ao escopo profissional e ao Meu Desempenho (que leem linhas
// por pessoa, inclusive comissão): mudança de contrato, fica para depois.
// ============================================================================
import { temFalhas, protegerCamposComFalha } from './avecFalhas.js';
import {
  chaveRange, chavesCompativeis, periodoPassado, podeReusarPassado, podeHerdar, lruDefinir, normalizarUnidades,
} from './rangeCacheRegras.js';

export function criarRangeStore({
  buscar, retentar, transferirBrutos = (de, para) => para, recalcularRede = (d) => d,
  enxuto = false, protegido = false, agora = () => Date.now(), hoje = () => new Date(), aoMudar = () => {},
} = {}) {
  const cache = new Map();
  const passado = new Map();
  const emVoo = new Map();
  const comTeto = enxuto || protegido;
  const definir = (mapa, k, v) => { if (comTeto) lruDefinir(mapa, k, v); else { mapa.set(k, v); } };
  const avisar = () => { try { aoMudar(Object.fromEntries(cache)); } catch { /* tela */ } };

  const varianteDe = (opts) => ({
    unidades: enxuto ? normalizarUnidades(opts.unidades) : null,
    withExtras0031: !!opts.withExtras0031,
    semPacotesPorProf: enxuto && !!opts.semPacotesPorProf,
  });
  const chaveDe = (s, e, v) => (enxuto ? chaveRange(s, e, v) : `${s}_${e}`);
  const compativeis = (s, e, v) => (enxuto ? chavesCompativeis(s, e, v) : [`${s}_${e}`]);

  function herdarDoAnterior(antigo, data) {
    if (!data?.units) return data;
    let mudou = false;
    const units = { ...data.units };
    for (const [k, u] of Object.entries(data.units)) {
      if (!u || u._error || !Array.isArray(u._falhas) || u._falhas.length === 0) continue;
      const nu = protegerCamposComFalha(antigo.units?.[k], u, { agora: agora(), aceitarHerdado: false });
      if (nu !== u) { units[k] = transferirBrutos(u, nu); mudou = true; }
    }
    return mudou ? recalcularRede({ ...data, units }) : data;
  }

  function guardar(k, s, e, data) {
    definir(cache, k, data);
    if (protegido && periodoPassado(e, hoje()) && !temFalhas(data)) definir(passado, k, { data, at: agora() });
    avisar();
  }

  function fetchRange(startDate, endDate, opts = {}) {
    const { force = false, maxAgeMs = 60_000 } = opts;
    const v = varianteDe(opts);
    const key = chaveDe(startDate, endDate, v);
    const ks = compativeis(startDate, endDate, v);
    if (protegido && periodoPassado(endDate, hoje())) {
      for (const k of ks) { const e = passado.get(k); if (podeReusarPassado(e, agora())) return Promise.resolve(e.data); }
    }
    if (!force) {
      for (const k of ks) {
        const ex = cache.get(k);
        if (!ex) continue;
        if (protegido && temFalhas(ex)) continue;
        const age = agora() - new Date(ex._fetchedAt).getTime();
        if (age < maxAgeMs) return Promise.resolve(ex);
      }
    }
    for (const k of ks) if (emVoo.has(k)) return emVoo.get(k);
    const antigo = cache.get(key);
    const optsSync = {
      skipTaxaRetorno: true,
      skipExtrasPorProf: true,
      withExtras0031: v.withExtras0031, // duelos: extras por prof via 0031 paginado
    };
    if (enxuto && v.unidades) optsSync.unidades = v.unidades;
    if (enxuto && v.semPacotesPorProf) optsSync.semPacotesPorProf = true;
    if (opts.prioridade != null) optsSync.prioridade = opts.prioridade;
    if (opts.prazoMs != null) optsSync.prazoMs = opts.prazoMs;
    const promessa = (async () => {
      try {
        let data = await buscar(startDate, endDate, optsSync);
        if (protegido && antigo && temFalhas(data) && podeHerdar(antigo, endDate, hoje())) data = herdarDoAnterior(antigo, data);
        guardar(key, startDate, endDate, data);
        return data;
      } finally {
        emVoo.delete(key);
      }
    })();
    emVoo.set(key, promessa);
    return promessa;
  }

  // "Tentar de novo": refaz só o que faltou (prioridade 0) e guarda no lugar.
  async function retentarFalhas(data, { prazoMs = 60_000 } = {}) {
    if (!data || !retentar) return data;
    const nova = await retentar(data, { prioridade: 0, prazoMs });
    if (nova && nova !== data && nova._startDate && nova._endDate) {
      const v = { ...(nova._variante || {}) };
      if (!enxuto) { v.unidades = null; v.semPacotesPorProf = false; }
      guardar(chaveDe(nova._startDate, nova._endDate, v), nova._startDate, nova._endDate, nova);
    }
    return nova;
  }

  return {
    fetchRange,
    retentarFalhas,
    estado: () => Object.fromEntries(cache),
    tamanho: () => ({ cache: cache.size, passado: passado.size, emVoo: emVoo.size }),
  };
}
